import { Cancel, CheckCircle, Download, DownloadForOffline, Info as InfoIcon, RateReview, Restore, Send } from '@mui/icons-material';
import { Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { FC, ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { usePrompt } from '../../../Views';
import { InvoiceHistoryButton } from '../../../Views/OrderReview/InvoiceHistoryButton';
import { AssignmentType } from '../../../dtos/generated/AssignmentType';
import { FreightBillDuplicateUsageDto, LineItemDto, MaterialLineItemDto, NumberValidationIndividualDto, NumberValidationMultipleDto, OrderReviewDto } from '../../../dtos';
import { ReviewStatus } from '../../../dtos/generated/ReviewStatus';
import { useArchiveOrderReviewDeleteFromDatabaseByIdMutation, useCreateFreightBillingLineItemValidateFreightBillingNumbersMutation, useCreateOrderDownloadSelectedOrdersMutation, useCreateOrderReviewPdfMutation, usePatchOrderReviewCancelMutation, usePatchOrderReviewRestoreByIdMutation } from '../../../store/generated/generatedApi';
import { exportToPdf, formatDate, LineItemTypes, QuoteType, toEasternDateKey } from '../../../util';
import { ContactSelect } from '../../CommonInputs';
import { ConfirmationDialogWithInput } from '../../Core/ConfirmDialog/ConfirmationDialogWithInput';
import { SlimFormHeader } from '../../Core/SlimFormHeader';
import { AuthenticatedComponent, FormInput, IFormProps, UserPermissionContext, useFailedActionSnackbar, useStandardSnackbar, useSuccessfulActionSnackbar, userHasPermissions } from '../../CoreLib/library';
import { OrderRelatedLinks } from '../OrderForm/OrderRelatedLinks';
import { AddOrderReviewLineItemDialog } from './AddOrderReviewLineItemDialog';
import { OrderReviewLineItemTable } from './OrderReviewLineItemTable';
import { QuickAddMaterialLineItemDialogForOrderReview } from './QuickAddMaterialLineItemDialogForOrderReview';
import { buildDuplicateContextListAction, buildDuplicateOrderReviewLinksAction } from './duplicateFreightBillSnackbarActions';
import { isMemoOnlyReviewEdit, useOrderReviewForm } from './useOrderReviewForm';

export interface IOrderReviewFormProps extends IFormProps<OrderReviewDto> {
    jobId: string;
    orderId: string;
}

export const OrderReviewForm: FC<IOrderReviewFormProps> = (props) => {  
    const { isLoading, initValues, jobId, orderId } = props;
    const { permissions } = useContext(UserPermissionContext);
    const isUserAllowedToReview = userHasPermissions(['create:review', 'edit:review'], permissions, 'or');
    const isUserAllowedToBill = userHasPermissions(['create:billing', 'edit:billing'], permissions, 'or');
    const isUserAllowedToEditReview = userHasPermissions(['edit:review'], permissions, 'and');
    const {
        isFormDirty,
        isFormValid,
        handleSave,
        handleSaveMemos,
        memoSaveResult,
        handleSubmit,
        handleCancel,
        baseFormErrors,
        lineItemErrors,
        dispatchErrors,
        freightErrors,
        handleContactChange,
        handleOrderedByContactChange,
        handleOrderLineItemsChange,
        handleAddNewDispatchReview,
        handleAddNewOrderReviewLineItem,
        handleAddNewFreightBillingLineItem,
        handleRemoveDispatchReview,
        handleRemoveOrderReviewLineItem,
        handleRemoveFreightBillingLineItem,
        handleIsTaxableChange,
        handlePoNumberChange,
        handleJobNumberChange,
        convertMaterialLineItemToOrderReviewLineItem,
        checkForDuplicateMaterial,
        getExcludedMaterialIdsForHaulingLineItem,
        formIsActive,
        formOrderNumber,
        formOrderDate,
        formOrderByContact,
        formContact,
        formOrderLineItems,
        formStatus,
        formPoNumber,
        formJobNumber,
        formIsTaxable,
        order,
    } = useOrderReviewForm(
        { ...props, onPendingRefreshApplied: () => setIsSendingToQuickBooks(false) },
        jobId,
        orderId
    );

    const [isAddLineItemDialogOpen, setIsAddLineItemDialogOpen] = useState(false);
    const [isMaterialSelectionDialogOpen, setIsMaterialSelectionDialogOpen] = useState(false);
    const [selectedHaulingLineItem, setSelectedHaulingLineItem] = useState<LineItemDto | undefined>(undefined);
    const [isSendingToQuickBooks, setIsSendingToQuickBooks] = useState(false);
    const [isCancelReviewMenuOpen, setIsCancelReviewMenuOpen] = useState(false);
    const [cancelReview, { isLoading: isCancelingReview }] = usePatchOrderReviewCancelMutation();
    const [restoreReview, { isLoading: isRestoringReview }] = usePatchOrderReviewRestoreByIdMutation();
    const [downloadSelectedPdfs, { isLoading: isDownloadingOrderPdf }] = useCreateOrderDownloadSelectedOrdersMutation();

    const handleCloseAddLineItemDialog = useCallback(() => {
        setIsAddLineItemDialogOpen(false);
    }, []);

    const handleAddOrderReviewLineItemClicked = useCallback(() => {
        setIsAddLineItemDialogOpen(true);
    }, []);

    const [isFreightBillNumberUniqueGloballyError, setIsFreightBillNumberUniqueGloballyError] = useState(false);
    const [isFreightBillNumberUniqueLocallyError, setIsFreightBillNumberUniqueLocallyError] = useState(false);
    const [duplicateFreightBillNumber, setDuplicateFreightBillNumber] = useState<string | undefined>();
    const [duplicateFreightBillUsages, setDuplicateFreightBillUsages] = useState<FreightBillDuplicateUsageDto[]>([]);
    const [localDuplicateContexts, setLocalDuplicateContexts] = useState<string[]>([]);
    const [validateFreightBillingNumbers] = useCreateFreightBillingLineItemValidateFreightBillingNumbersMutation();

    const globalDuplicateLinksAction = useMemo(() => {
        return buildDuplicateOrderReviewLinksAction(duplicateFreightBillUsages);
    }, [duplicateFreightBillUsages]);

    const localDuplicateContextAction = useMemo(() => {
        return buildDuplicateContextListAction(
            localDuplicateContexts.map((context) => ({ label: context })),
            'Local duplicate locations:'
        );
    }, [localDuplicateContexts]);

    useStandardSnackbar(isFreightBillNumberUniqueGloballyError, `Duplicate Freight Bill #${duplicateFreightBillNumber ?? ''}.`, 'warning', () => {
        setIsFreightBillNumberUniqueGloballyError(false);
        setDuplicateFreightBillNumber(undefined);
        setDuplicateFreightBillUsages([]);
    }, { autoHideDuration: 7000, action: globalDuplicateLinksAction });

    useStandardSnackbar(isFreightBillNumberUniqueLocallyError, `Duplicate Freight Bill #${duplicateFreightBillNumber ?? ''} in this Order Review.`, 'warning', () => {
        setIsFreightBillNumberUniqueLocallyError(false);
        setDuplicateFreightBillNumber(undefined);
        setDuplicateFreightBillUsages([]);
        setLocalDuplicateContexts([]);
    }, { autoHideDuration: 7000, action: localDuplicateContextAction });

    const handleUpdatedFreightBillingNumber = useCallback(async (updatedFreightBillingNumber: string, orderLineItemIndex: number, dispatchIndex: number, freightBillingLineItemIndex: number) => {
        const formFreightBillingNumbers = formOrderLineItems.flatMap((lineItem, lineItemIdx) =>
            lineItem.dispatches.flatMap((dispatchReview, dispatchIdx) =>
                dispatchReview.freightBillingLineItems?.map((freightItem, freightIdx) =>
                    lineItemIdx === orderLineItemIndex &&
                        dispatchIdx === dispatchIndex &&
                        freightIdx === freightBillingLineItemIndex
                        ? { ...freightItem, freightBillNumber: updatedFreightBillingNumber }
                        : freightItem
                )
            )
        );
        // To handle the local changes that can happen without saving the form, the above code maps out all current freight billing numbers and updates the one that matches the indices.
        // This ensures that ALL local changes are reflected in the formFreightBillingNumbers array and we can check for uniqueness both globally and locally.

        if (updatedFreightBillingNumber === '') {
            return; // If the user deletes the freight bill number, we don't need to check for uniqueness.
        }


        if (formFreightBillingNumbers.filter((item) => item?.freightBillNumber === updatedFreightBillingNumber).length > 1) {
            const currentOrderLineItemNumber = formOrderLineItems[orderLineItemIndex]?.orderLineItemNumber;
            const localContexts = formOrderLineItems
                .flatMap((lineItem, lineItemIdx) =>
                    lineItem.dispatches.flatMap((dispatchReview, dispatchIdx) =>
                        (dispatchReview.freightBillingLineItems ?? []).map((freightItem, freightIdx) => ({
                            freightBillNumber: lineItemIdx === orderLineItemIndex && dispatchIdx === dispatchIndex && freightIdx === freightBillingLineItemIndex
                                ? updatedFreightBillingNumber
                                : freightItem.freightBillNumber,
                            orderLineItemNumber: lineItem.orderLineItemNumber,
                            dispatchNumber: dispatchIdx + 1,
                            freightLineItemNumber: freightIdx + 1,
                            isCurrentField: lineItemIdx === orderLineItemIndex && dispatchIdx === dispatchIndex && freightIdx === freightBillingLineItemIndex,
                        }))
                    )
                )
                .filter((item) =>
                    item.freightBillNumber === updatedFreightBillingNumber &&
                    !item.isCurrentField &&
                    item.orderLineItemNumber !== currentOrderLineItemNumber
                )
                .map((item) => `Also used on Line Item #${item.orderLineItemNumber}, Dispatch #${item.dispatchNumber}, Freight Row #${item.freightLineItemNumber}`);

            setIsFreightBillNumberUniqueLocallyError(true);
            setDuplicateFreightBillNumber(updatedFreightBillingNumber);
            setDuplicateFreightBillUsages([]);
            setLocalDuplicateContexts(localContexts);
        } else {
            const numbersToValidate: NumberValidationMultipleDto = {
                numberValidations: [
                    {
                        number: updatedFreightBillingNumber,
                        isValid: false
                    }
                ] as NumberValidationIndividualDto[]
            };
            const response = await validateFreightBillingNumbers(numbersToValidate).unwrap();
            const matchedNumberValidation = response.numberValidations.find((item) => item.number === updatedFreightBillingNumber);
            if (!matchedNumberValidation?.isValid) {
                setIsFreightBillNumberUniqueGloballyError(true);
                setDuplicateFreightBillNumber(updatedFreightBillingNumber);
                setDuplicateFreightBillUsages(matchedNumberValidation?.duplicateUsages ?? []);
            } else {
                setIsFreightBillNumberUniqueGloballyError(false);
                setIsFreightBillNumberUniqueLocallyError(false);
                setDuplicateFreightBillNumber(undefined);
                setDuplicateFreightBillUsages([]);
                setLocalDuplicateContexts([]);
            }
        }
    }, [formOrderLineItems, validateFreightBillingNumbers]);

    const currentQuote = useMemo(() => {
        return order?.quote ?? initValues?.job?.currentQuote;
    }, [initValues?.job?.currentQuote, order?.quote]);

    const handleAddQuoteLineItem = useCallback(
        (lineItem: LineItemDto) => {
            // Check if it's a SiteHauling line item and quote is Standard
            if (lineItem.type === LineItemTypes.SiteHauling && currentQuote?.type === QuoteType.Standard) {
                // Open material selection dialog
                setSelectedHaulingLineItem(lineItem);
                setIsMaterialSelectionDialogOpen(true);
                handleCloseAddLineItemDialog();
            } else {
                // Add line item directly (existing behavior)
                handleAddNewOrderReviewLineItem(lineItem);
                handleCloseAddLineItemDialog();
            }
        },
        [handleCloseAddLineItemDialog, handleAddNewOrderReviewLineItem, currentQuote?.type]
    );

    const handleCloseMaterialSelectionDialog = useCallback(() => {
        setIsMaterialSelectionDialogOpen(false);
        setSelectedHaulingLineItem(undefined);
    }, []);

    const handleAddMaterialLineItems = useCallback(
        async (materialLineItem: MaterialLineItemDto) => {
            // Convert MaterialLineItemDto to OrderReviewLineItemDto
            const materialOrderReviewLineItem = await convertMaterialLineItemToOrderReviewLineItem(
                materialLineItem as LineItemDto,
                formOrderLineItems.length + 1
            );
            // Add only the material line item
            handleOrderLineItemsChange((current) => [...current, materialOrderReviewLineItem]);
            handleCloseMaterialSelectionDialog();
        },
        [handleCloseMaterialSelectionDialog, handleOrderLineItemsChange, convertMaterialLineItemToOrderReviewLineItem, formOrderLineItems.length]
    );

    const handleAddCustomLineItem = useCallback(() => {
        handleAddNewOrderReviewLineItem();
        handleCloseAddLineItemDialog();
    }, [handleCloseAddLineItemDialog, handleAddNewOrderReviewLineItem]);

    const [getInvoicePdf, { isSuccess, isError, error: pdfGenerationError, isLoading: pdfLoading, reset: resetCreatePDf }] = useCreateOrderReviewPdfMutation();
    useSuccessfulActionSnackbar('created', 'quote PDF', isSuccess, resetCreatePDf);
    useFailedActionSnackbar('create', 'quote PDF', isError, resetCreatePDf, pdfGenerationError);
    useSuccessfulActionSnackbar('saved', 'memos', memoSaveResult.isSuccess, memoSaveResult.reset);
    useFailedActionSnackbar('save', 'memos', memoSaveResult.isError, memoSaveResult.reset, memoSaveResult.error);

    usePrompt('Are you sure you want to leave this page?\nUnsaved changes will be lost.', isFormDirty());

    const currentOrderReviewId = useMemo(() => {
        return initValues?.id;
    }, [initValues?.id]);

    const handleCancelReview = useCallback(
        (cancellationReason: string) => {
            setIsCancelReviewMenuOpen(false);
            if (currentOrderReviewId) {
                cancelReview({ id: currentOrderReviewId, reason: cancellationReason });
            }
        },
        [cancelReview, currentOrderReviewId]
    );

    const handleRestoreReview = useCallback(() => {
        if (currentOrderReviewId) {
            restoreReview({ id: currentOrderReviewId });
        }
    }, [restoreReview, currentOrderReviewId]);

    const generatePdf = useCallback(() => {
        if (!pdfLoading && currentOrderReviewId) {
            getInvoicePdf({ id: currentOrderReviewId, isPreview: true }).then((response: any) => {
                if (response?.data) {
                    window.open(response?.data.url, '_blank');
                }
            });
        }
    }, [currentOrderReviewId, getInvoicePdf, pdfLoading]);

    const handleDownloadOrder = useCallback(() => {
        if (isDownloadingOrderPdf || !orderId) {
            return;
        }
        // The order PDF endpoint sources from the Order Review whenever one exists,
        // so passing the orderId here prints the review-updated order (RK-7158).
        downloadSelectedPdfs({
            orderIds: [orderId],
            siteId: undefined,
            orderDate: undefined,
        }).then((response: any) => {
            if (response.data) {
                exportToPdf(response.data, `Order-${formOrderNumber}-${toEasternDateKey(formOrderDate ?? new Date())}.pdf`.replace(/\s+/g, ''));
            }
        });
    }, [downloadSelectedPdfs, isDownloadingOrderPdf, orderId, formOrderNumber, formOrderDate]);

    const gridItem = useCallback((content: ReactElement) => {
        return (
            <Grid
                item
                sx={{
                    '--Grid-borderWidth': '1px',
                    border: 'var(--Grid-borderWidth) solid',
                    borderColor: 'divider',
                    borderCollapse: 'collapse',
                    flexGrow: '1',
                }}>
                <Box p={2}>{content}</Box>
            </Grid>
        );
    }, []);

    const isCanceled = formStatus === ReviewStatus.Canceled;
    const isMemoOnlyEdit = isMemoOnlyReviewEdit(formStatus, isUserAllowedToEditReview, isUserAllowedToBill);

    const editDisabled = useMemo(() => {
        if (pdfLoading || isLoading || isSendingToQuickBooks || isCanceled) {
            return true;
        }

        return false;
    }, [isLoading, pdfLoading, isSendingToQuickBooks, isCanceled]);

    // Locks every field except the memos; editDisabled alone still governs the memos and Delete.
    const fieldsLocked = editDisabled || isMemoOnlyEdit;

    const saveDisabled = useMemo(() => {
        if (pdfLoading || isLoading || isSendingToQuickBooks || isCanceled || memoSaveResult.isLoading) {
            return true;
        }
        if (formStatus === ReviewStatus.Invoiced && !isUserAllowedToBill && !isMemoOnlyEdit) {
            return true;
        }

        return false;
    }, [formStatus, isLoading, isUserAllowedToBill, isMemoOnlyEdit, pdfLoading, isSendingToQuickBooks, isCanceled, memoSaveResult.isLoading]);

    const canSubmit = useMemo(() => {
        if (!isFormValid || isLoading || isSendingToQuickBooks || isCanceled) {
            return false;
        }
        if (isUserAllowedToBill) {
            if (!initValues?.readyForBilling) {
                return true;
            }
            const freightBillingLineItems = formOrderLineItems
                .filter((lineItem) => !lineItem.canceledOn)
                .flatMap((lineItem) =>
                    lineItem.dispatches.flatMap((dispatchReview) => dispatchReview.freightBillingLineItems)
                );

            if (
                initValues?.readyForBilling &&
                (initValues.invoiceStatus === 'Awaiting Review' || freightBillingLineItems.some((lineItem) => !lineItem!.invoiceId))
            ) {
                return true;
            } else {
                return false;
            }
        }
        if (isUserAllowedToReview) {
            if (!initValues?.readyForBilling && formStatus === ReviewStatus.InReview) {
                return true;
            } else {
                return false;
            }
        }

        return false;
    }, [
        isFormValid,
        isLoading,
        isUserAllowedToBill,
        isUserAllowedToReview,
        isSendingToQuickBooks,
        initValues?.readyForBilling,
        initValues?.invoiceStatus,
        formOrderLineItems,
        formStatus,
        isCanceled,
    ]);

    const submit = useCallback(() => {
        setIsSendingToQuickBooks(true);
        const result = handleSubmit(initValues?.readyForBilling ?? false);
        return Promise.resolve(result).catch(() => {
            setIsSendingToQuickBooks(false);
        });
    }, [handleSubmit, initValues?.readyForBilling]);

    

    const cityStateZip = useMemo(() => {
        if (currentQuote) {
            return `${currentQuote?.city}, ${currentQuote?.state}, ${currentQuote?.zipCode}`;
        } else {
            return '';
        }
    }, [currentQuote]);

    const reviewTitle = useMemo(() => {
        const customerName = currentQuote?.customer?.name || '';
        const customerNumber = currentQuote?.customer?.customerNumber || '';
        return `${customerNumber}: ${customerName} (Order #${formOrderNumber ?? '###'})`;
    }, [formOrderNumber, currentQuote?.customer?.name, currentQuote?.customer?.customerNumber]);

    const reviewTabTitle = useMemo(() => {
        const customerNumber = currentQuote?.customer?.customerNumber || '';
        return `Review #${formOrderNumber ?? '###'}: Cust #${customerNumber}`;
    }, [formOrderNumber, currentQuote?.customer?.customerNumber]);

    const billingCheck = useMemo(() => {
        const missingDispatchLabels: string[] = [];
        let totalDispatches = 0;
        let enteredDispatches = 0;

        formOrderLineItems.forEach((lineItem, lineItemIndex) => {
            if (lineItem.canceledOn) {
                return;
            }
            lineItem.dispatches.forEach((dispatchReview, dispatchIndex) => {
                totalDispatches += 1;
                const hasBillingInfo = (dispatchReview.freightBillingLineItems?.length ?? 0) > 0;

                if (hasBillingInfo) {
                    enteredDispatches += 1;
                    return;
                }

                const lineItemNumber = lineItem.orderLineItemNumber ?? lineItemIndex + 1;
                const dispatchNumber = dispatchIndex + 1;
                const assignmentName = dispatchReview.assignmentType === AssignmentType.Broker
                    ? dispatchReview.broker?.code && dispatchReview.broker?.name
                        ? `${dispatchReview.broker.code} - ${dispatchReview.broker.name}`
                        : 'Broker not selected'
                    : dispatchReview.driver?.code && dispatchReview.driver?.name
                        ? `${dispatchReview.driver.code} - ${dispatchReview.driver.name}`
                        : 'Driver not selected';

                missingDispatchLabels.push(`Line Item #${lineItemNumber}, Dispatch #${dispatchNumber} - ${assignmentName}`);
            });
        });

        return {
            totalDispatches,
            enteredDispatches,
            missingDispatchLabels,
        };
    }, [formOrderLineItems]);

    return (
        <Box display='flex' flexDirection='column'>
            <SlimFormHeader
                objectName='Order'
                permissionsTypeName='order'
                icon={<RateReview />}
                title={reviewTitle}
                tabTitle={reviewTabTitle}
                titleMaxWidth="60vw"
                breadcrumbs={[
                    { label: 'Home', navLink: '/' },
                    {
                        label: 'Review',
                        navLink: `/orderReview?date=${toEasternDateKey(initValues?.orderDate ?? new Date())} `,
                    },
                ]}
                isActive={formIsActive}
                id={currentOrderReviewId}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={isMemoOnlyEdit ? handleSaveMemos : handleSave}
                // A memo-only save sends nothing else, so the rest of the form's validation doesn't apply to it.
                canSave={!saveDisabled && (isMemoOnlyEdit || isFormValid)}
                canDelete={!editDisabled}
                entityNameSingular={'order'}
                hideDividers
                deleteConfirmationDialog={{
                    title: 'Delete Order Review?',
                    content: (
                        <Typography>
                            Are you sure you would like to permanently delete this record? It will be deleted immediately and cannot be recovered.
                        </Typography>
                    ),
                }}
                deleteMutation={useArchiveOrderReviewDeleteFromDatabaseByIdMutation}
                submit={{
                    handleSubmit: () => submit(),
                    canSubmit: canSubmit,
                    submitText: !initValues?.readyForBilling ? 'Send to Billing' : 'Send to QuickBooks',
                    icon: !initValues?.readyForBilling ? <CheckCircle /> : <Send />,
                }}
                additionalHeaderItems={
                    <>
                        {initValues?.id && (
                            !isCanceled ? (
                                <AuthenticatedComponent
                                    requiredPermissions={['cancel:orders']}
                                    logic='and'
                                    children={
                                        <Button
                                            startIcon={<Cancel />}
                                            variant='contained'
                                            disabled={isCancelingReview || isRestoringReview || initValues?.isCancelable === false}
                                            onClick={() => setIsCancelReviewMenuOpen(true)}>
                                            Cancel Review
                                        </Button>
                                    }
                                />
                            ) : (
                                <AuthenticatedComponent
                                    requiredPermissions={['cancel:orders']}
                                    logic='and'
                                    children={
                                        <Button
                                            startIcon={<Restore />}
                                            variant='contained'
                                            disabled={isCancelingReview || isRestoringReview}
                                            onClick={handleRestoreReview}>
                                            Restore Review
                                        </Button>
                                    }
                                />
                            )
                        )}
                        <Button
                            startIcon={<Download />}
                            variant='contained'
                            disabled={!orderId || isDownloadingOrderPdf}
                            onClick={handleDownloadOrder}>
                            Download
                        </Button>
                        <Tooltip title='Preview Invoice'>
                            {!pdfLoading ? (
                                <Box>
                                    <IconButton onClick={generatePdf} disabled={!initValues || pdfLoading || isCanceled}>
                                        <DownloadForOffline />
                                    </IconButton>
                                </Box>
                            ) : (
                                <CircularProgress />
                            )}
                        </Tooltip>
                        <InvoiceHistoryButton orderReviewId={initValues?.id} size='small' disabled={isCanceled} />
                    </>
                }
            />
            <Paper sx={{ display: 'flex', p: 2, mx: 2, mt: 2, alignItems: 'center' }}>
                <Grid item xs={12} display='flex' gap={1} alignItems='center' flexWrap='wrap' justifyContent='center'>
                    {order &&
                        <OrderRelatedLinks order={order} billingCheck={billingCheck} />
                    }
                </Grid>
            </Paper>
            <Paper sx={{ display: 'flex', flexGrow: 1, overflow: 'auto', flexDirection: 'column', p: 2, m: 2 }}>
                <Grid container direction='row' spacing={2} alignItems='center' pt={2}>
                    <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={8} md={8}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} ml={2}>
                                    <FormInput
                                        label='Project Name'
                                        name='projectName'
                                        value={currentQuote?.projectName ?? ''}
                                        onChange={() => { }}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={12} ml={2}>
                                    <FormInput
                                        label='Job Name'
                                        name='jobName'
                                        value={currentQuote?.jobName ?? ''}
                                        onChange={() => { }}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={4} md={4} container justifyContent="center">
                            <Grid item xs={12} ml={2}>
                                {gridItem(
                                    <>
                                        <Typography>Project Address:</Typography>
                                        <Typography>{currentQuote?.addressLine1 ?? ''}</Typography>
                                        {currentQuote?.addressLine2 && <Typography>{currentQuote?.addressLine2}</Typography>}
                                        <Typography>{cityStateZip}</Typography>
                                        <Typography>{currentQuote?.country}</Typography>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={3} sm={0.5} md={0.5} display="flex" justifyContent="center" alignItems="center">
                        <Tooltip
                            title={
                                <Box>
                                    <Typography variant="body2" fontWeight="bold">Orderer: {order?.orderer ?? 'N/A'}</Typography>
                                    <Typography variant="body2" fontWeight="bold">Order Date: {formOrderDate ? formatDate(formOrderDate) : 'N/A'}</Typography>
                                    <Typography variant="body2" fontWeight="bold">Created On: {order?.createdOn ? formatDate(order.createdOn, 'Pp') : 'N/A'}</Typography>
                                </Box>
                            }
                            arrow
                            placement="top"
                        >
                            <Box display="flex" alignItems="center" >
                                <InfoIcon color="action" fontSize="medium" />
                            </Box>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormInput
                            value={formPoNumber}
                            onChange={(event) => handlePoNumberChange(event.target.value)}
                            label='PO Number'
                            required
                            fullWidth
                            disabled={fieldsLocked} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormInput
                            value={formJobNumber}
                            onChange={(event) => handleJobNumberChange(event.target.value)}
                            label='Job Number'
                            fullWidth
                            disabled={fieldsLocked} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={1} mt={1}>
                        <FormControl fullWidth disabled={fieldsLocked}>
                            <FormControlLabel
                                control={<Checkbox disabled={fieldsLocked} checked={formIsTaxable} onChange={handleIsTaxableChange} />}
                                label='Is Taxable'
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <ContactSelect
                            label='Ordered By'
                            customerId={currentQuote?.customerId}
                            selectedContactId={formOrderByContact?.id ?? ''}
                            handleSelectedContactChange={handleOrderedByContactChange}
                            errorMessage={baseFormErrors.get('orderedByContact')}
                            disabled={fieldsLocked}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <ContactSelect
                            customerId={currentQuote?.customerId}
                            selectedContactId={formContact?.id ?? ''}
                            handleSelectedContactChange={handleContactChange}
                            errorMessage={baseFormErrors.get('contact')}
                            disabled={fieldsLocked}
                        />
                    </Grid>
                </Grid>
                <Typography variant='h2' mt={1}>
                    Order Line Items
                </Typography>
                <Box flexGrow={1} overflow='hidden' minHeight={200}>
                    {order && (
                        <OrderReviewLineItemTable
                            disabled={fieldsLocked}
                            memosDisabled={editDisabled}
                            orderLineItems={formOrderLineItems}
                            order={order}
                            addDispatchReview={handleAddNewDispatchReview}
                            removeDispatchReview={handleRemoveDispatchReview}
                            setOrderLineItems={handleOrderLineItemsChange}
                            removeOrderReviewLineItem={handleRemoveOrderReviewLineItem}
                            addFreightBillingLineItem={handleAddNewFreightBillingLineItem}
                            removeFreightBillingLineItem={handleRemoveFreightBillingLineItem}
                            addOrderReviewLineItem={handleAddOrderReviewLineItemClicked}
                            handleUpdatedFreightBillingNumber={handleUpdatedFreightBillingNumber}
                            lineItemErrors={lineItemErrors}
                            dispatchErrors={dispatchErrors}
                            freightErrors={freightErrors}
                            orderPoNumber={formPoNumber}
                            orderJobNumber={formJobNumber}
                            isOrderReviewTaxable={formIsTaxable}
                        />
                    )}
                </Box>
            </Paper>
            {currentQuote && (
                <>
                    <AddOrderReviewLineItemDialog
                        open={isAddLineItemDialogOpen}
                        onClose={handleCloseAddLineItemDialog}
                        quote={currentQuote}
                        onAddCustomLineItem={handleAddCustomLineItem}
                        onAddQuoteLineItem={handleAddQuoteLineItem}
                    />
                    {selectedHaulingLineItem && (
                        <QuickAddMaterialLineItemDialogForOrderReview
                            open={isMaterialSelectionDialogOpen}
                            onClose={handleCloseMaterialSelectionDialog}
                            onAdd={handleAddMaterialLineItems}
                            siteHaulingLineItem={selectedHaulingLineItem}
                            checkForDuplicateMaterial={checkForDuplicateMaterial}
                            getExcludedMaterialIdsForHaulingLineItem={getExcludedMaterialIdsForHaulingLineItem}
                            customerName={currentQuote?.customer?.name ?? ''}
                        />
                    )}
                </>
            )}
            <ConfirmationDialogWithInput
                open={isCancelReviewMenuOpen}
                onClose={() => setIsCancelReviewMenuOpen(false)}
                formLabel={'Please enter a cancelation reason.'}
                title={'Cancel Review'}
                titleIcon={<Cancel />}
                cancelText={'Cancel'}
                confirmText={'Save'}
                save={handleCancelReview}
                requireConfirmation={false}
            />
        </Box>
    );
};
